import { Box, CircularProgress } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { getVariables } from "../../../api/variables";
import { useCaptureMonitoring } from "../../../hooks/Capture";
import { VariableResponse } from "../../../types/model/variable";
import { VARIABLE_CATEGORY_CODES } from "../../../types/model/variable/VariableCategories";
import CaptureMonitoringRecords from "../Monitoring/CaptureMonitoringRecords";
import MonitoringChart from "../Monitoring/MonitoringChart";

interface MonitoringTabProps {
  captureId: string;
  captureName: string;
  handleError: (error: any) => void;
  canEdit: boolean;
}

const MonitoringTab: React.FC<MonitoringTabProps> = ({ captureId, captureName, handleError, canEdit }) => {
  const { 
    monitoringRecordsData, 
    isPending,
    refetch,
    filters, 
    setFilters, 
    createMonitoring, 
    updateMonitoring, 
    deleteMonitoring,
    page,
    setPage 
  } = useCaptureMonitoring(captureId);
  const [isChartMaximized, setIsChartMaximized] = useState(false);
  const [variables, setVariables] = useState<VariableResponse[]>([]);

  useEffect(() => {
    const fetchVariables = async () => {
      try {
        const data = await getVariables(false, VARIABLE_CATEGORY_CODES.MONITORING);
        setVariables(data);
      } catch (error) {
        handleError(error);
      }
    };
    fetchVariables();
  }, [handleError]);

  const filteredData = useMemo(() => {
    if (!filters.variableId) return monitoringRecordsData;
    return monitoringRecordsData.filter((record) => record.variableId === filters.variableId);
  }, [monitoringRecordsData, filters.variableId]);

  if (isPending && monitoringRecordsData.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const toggleMapMaximization = () => {
    setIsChartMaximized((prev) => !prev);
  };

  return (
    <>
      <CaptureMonitoringRecords
        captureId={Number(captureId)}
        monitoringRecordsData={filteredData}
        canEdit={canEdit}
        variables={variables}
        filters={filters}
        onApplyFilters={setFilters}
        onCreate={async (values) => {
          await createMonitoring(values).catch(handleError);
        }}
        onUpdate={async (id, values) => {
          await updateMonitoring({ monitoringId: id, values }).catch(handleError);
        }}
        onDelete={async (id) => {
          await deleteMonitoring(id).catch(handleError);
        }}
        onRequestRefresh={refetch}
        isChartMaximized={isChartMaximized}
        toggleMapMaximization={toggleMapMaximization}
        page={page}
        onPageChange={setPage}
      />
      <MonitoringChart
        monitoringRecordsData={filteredData}
        variables={variables}
        isChartMaximized={isChartMaximized}
        captureName={captureName}
        selectedVariableId={filters.variableId}
      />
    </>
  );
};

export default MonitoringTab;
